import { useMemo } from 'react';
import NextLink from 'next/link';
import {
  Box,
  Button,
  Flex,
  Heading,
  Link,
  List,
  ListIcon,
  ListItem,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { CheckCircleIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { ChecklistSection, MarketplaceInfo } from 'src/api/v1-api';
import { CONTAINER_MAX_WIDTH_1 } from 'src/constants/ui';

interface PreviewLaunchChecklistProps {
  marketplace: MarketplaceInfo | null;
}

const PreviewLaunchChecklist = ({ marketplace }: PreviewLaunchChecklistProps) => {
  const adminBasePath = '/admin';

  const checklistSections = useMemo<ChecklistSection[]>(
    () => [
      {
        heading: 'Customize the basics',
        items: [
          {
            title: 'Brand your site',
            description:
              'Update your name, logo, and colors, so visitors land on a consistent, on-brand experience.',
            href: `/customize`,
            linkLabel: 'Edit site info',
          },
          {
            title: 'Connect your social accounts',
            description:
              'Link your social media accounts so Loma can automatically help promote your site and engage with your audience.',
            href: `/social-connect`,
            linkLabel: 'Configure social accounts',
          },
        ],
      },
    ],
    [adminBasePath],
  );

  const containerBg = useColorModeValue('green.50', 'green.900');
  const borderColor = useColorModeValue('green.200', 'green.500');
  const headingColor = useColorModeValue('green.900', 'green.100');
  const textColor = useColorModeValue('gray.700', 'gray.100');

  if (marketplace?.status !== 'preview') {
    return null;
  }

  return (
    <Box
      data-test="preview-launch-checklist"
      border="1px solid"
      borderColor={borderColor}
      bg={containerBg}
      borderRadius="xl"
      px={{ base: 5, md: 8 }}
      py={{ base: 5, md: 7 }}
      mt={{ base: 4, md: 6 }}
      mx="auto"
      maxW={CONTAINER_MAX_WIDTH_1}
      boxShadow="lg"
    >
      <Box mb={{ base: 3, md: 4 }}>
        <Heading as="h2" size="md" color={headingColor} mb={2}>
          Before you launch
        </Heading>
        <Text color={textColor} fontSize="sm">
          Use this checklist to prepare your site for launch.
        </Text>
      </Box>

      <Stack spacing={{ base: 5, md: 6 }}>
        {checklistSections.map((section) => (
          <Box key={section.heading}>
            <Heading as="h3" size="sm" color={headingColor} mb={2}>
              {section.heading}
            </Heading>
            <List spacing={3}>
              {section.items.map((item) => (
                <ListItem key={`${section.heading}-${item.title}`}>
                  <Flex align={{ base: 'flex-start', md: 'center' }} gap={3} wrap="wrap">
                    <ListIcon as={CheckCircleIcon} color="green.500" mt={{ base: 1, md: 0 }} />
                    <Box>
                      <Text fontWeight="semibold" color={headingColor}>
                        {item.title}
                      </Text>
                      <Text color={textColor} fontSize="sm">
                        {item.description}{' '}
                        {item.href && (
                          <Link
                            as={NextLink}
                            href={item.href}
                            color="green.700"
                            fontWeight="semibold"
                            display="inline-flex"
                            alignItems="center"
                            gap={1}
                            isExternal={item.isExternal}
                          >
                            {item.linkLabel ?? 'Open link'}
                            {item.isExternal && <ExternalLinkIcon boxSize={3} />}
                          </Link>
                        )}
                      </Text>
                    </Box>
                  </Flex>
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Stack>

      <Flex justify="center" mt={6}>
        <Button onClick={() => (window.location.href = '/pricing')} colorScheme="green" size="lg">
          Start Trial and Launch
        </Button>
      </Flex>
    </Box>
  );
};

export default PreviewLaunchChecklist;
